import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useIsReadOnly, useProfile } from '@/shared/auth/SessionProvider';
import {
  crearNotaImportacion,
  eliminarNotaImportacion,
  listNotasImportacion,
  type NotaImportacion,
} from '@/shared/api/controllers/importaciones';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Textarea } from '@/shared/components/ui/textarea';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { IconAction } from './componentes';

const MAX_NOTA = 2000;

/** dd/mm/yyyy HH:mm en hora argentina (el server guarda UTC). */
function fechaHora(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-AR', {
    timeZone: 'America/Argentina/Buenos_Aires',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Las notas de la importación. La usa también la pestaña para mostrar el contador. */
export function useNotasImportacion(importacionId: number) {
  const { config: activeCountry } = useActiveCountry();
  const key = ['importaciones', activeCountry.code, 'notas', importacionId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => listNotasImportacion(importacionId),
    staleTime: 15_000,
  });
  return { q, key };
}

/**
 * Bitácora de la importación: notas con fecha, usuario y texto, la más nueva
 * primero. Las puede borrar su autor o un admin. Sin la tabla (migración sin
 * aplicar) el server avisa con `habilitado: false` y la sección no se muestra.
 */
export function BitacoraImportacion({ importacionId }: { importacionId: number }) {
  const qc = useQueryClient();
  const profile = useProfile();
  const readOnly = useIsReadOnly();
  const [texto, setTexto] = useState('');
  const [borrando, setBorrando] = useState<NotaImportacion | null>(null);
  const { q: notasQ, key } = useNotasImportacion(importacionId);
  const refrescar = () => qc.invalidateQueries({ queryKey: key });

  const agregar = useMutation({
    mutationFn: () => crearNotaImportacion(importacionId, texto.trim()),
    onSuccess: () => {
      setTexto('');
      refrescar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo agregar la nota.'),
  });
  const borrar = useMutation({
    mutationFn: (n: NotaImportacion) => eliminarNotaImportacion(importacionId, n.id),
    onSuccess: () => {
      setBorrando(null);
      refrescar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo borrar la nota.'),
  });

  // Sin migración no hay bitácora; si falla la carga tampoco se estorba al detalle.
  if (notasQ.isPending || notasQ.isError || !notasQ.data.habilitado) return null;
  const notas = notasQ.data.items;
  const puedeBorrar = (n: NotaImportacion) =>
    !readOnly && (profile?.perfil === 'admin' || n.usuario === profile?.usuario_app);

  return (
    <section>
      <div className="space-y-3 rounded-2xl border bg-card p-4">
        {!readOnly && (
          <div className="space-y-2">
            <Textarea
              variant="filled"
              rows={2}
              maxLength={MAX_NOTA}
              placeholder="Anotá novedades, avisos del fabricante, cambios de fecha…"
              aria-label="Nueva nota"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => agregar.mutate()}
                disabled={texto.trim() === '' || agregar.isPending}
              >
                Agregar nota
              </Button>
            </div>
          </div>
        )}
        {notas.length === 0 ? (
          <p className="rounded-lg border border-dashed bg-card/40 px-3 py-2 text-xs text-muted-foreground">Sin notas todavía.</p>
        ) : (
          <ul className="divide-y divide-border">
            {notas.map((n) => (
              <li key={n.id} className="flex items-start gap-3 py-2 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground">
                    <span className="tabular-nums">{fechaHora(n.creadaEn)}</span> · {n.usuario || '—'}
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-foreground">{n.texto}</p>
                </div>
                {puedeBorrar(n) && (
                  <IconAction
                    title="Borrar nota"
                    destructive
                    onClick={() => setBorrando(n)}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </IconAction>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={borrando != null} onOpenChange={(o) => !o && !borrar.isPending && setBorrando(null)}>
        <DialogContent className="max-w-md" processing={borrar.isPending && 'Borrando…'}>
          <DialogHeader>
            <DialogTitle>Borrar nota</DialogTitle>
            <DialogDescription>
              {borrando && `Se borra la nota del ${fechaHora(borrando.creadaEn)} de ${borrando.usuario || '—'}.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBorrando(null)} disabled={borrar.isPending}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => borrando && borrar.mutate(borrando)}
              disabled={borrar.isPending}
            >
              Borrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
